import React, { useEffect, useState } from 'react';
import { Container, Table, Button, Modal, Form, Badge, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';
import ImageUpload from '../../components/ImageUpload';
import { categoriesAPI } from '../../services/api';
import { toast } from 'react-toastify';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        image: category.image || '',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        image: '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submitting category data:', formData);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory._id, formData);
        toast.success('Category updated successfully');
      } else {
        const response = await categoriesAPI.create(formData);
        console.log('Category created:', response);
        toast.success('Category created successfully');
      }
      setShowModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Category submission error:', error);
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoriesAPI.delete(id);
        toast.success('Category deleted successfully');
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await categoriesAPI.toggleActive(id);
      toast.success('Category status updated');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleUpdateCounts = async () => {
    try {
      await categoriesAPI.updateCounts();
      toast.success('Product counts updated');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update counts');
    }
  };

  return (
    <AdminLayout>
      <Container fluid className="my-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Category Management</h1>
          <div>
            <Button variant="secondary" className="me-2" onClick={handleUpdateCounts}>
              Update Product Counts
            </Button>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <FaPlus /> Add Category
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Description</th>
                <th>Products</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category._id}>
                  <td>
                    {category.image ? (
                      <img 
                        src={category.image} 
                        alt={category.name} 
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                      />
                    ) : (
                      <div 
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          backgroundColor: '#e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        N/A
                      </div>
                    )}
                  </td>
                  <td>{category.name}</td>
                  <td><small className="text-muted">{category.slug}</small></td>
                  <td>{category.description || '-'}</td>
                  <td><Badge bg="info">{category.productCount}</Badge></td>
                  <td>
                    <Badge bg={category.isActive ? 'success' : 'secondary'}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    <Button 
                      variant="warning" 
                      size="sm" 
                      className="me-2" 
                      onClick={() => handleShowModal(category)}
                    >
                      <FaEdit />
                    </Button>
                    <Button 
                      variant={category.isActive ? 'secondary' : 'success'}
                      size="sm" 
                      className="me-2"
                      onClick={() => handleToggleActive(category._id)}
                    >
                      {category.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => handleDelete(category._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingCategory ? 'Edit Category' : 'Add Category'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Category Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Windows, Office, Antivirus"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this category"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <ImageUpload
                  currentImage={formData.image}
                  onImageChange={(imageUrl) => setFormData({ ...formData, image: imageUrl })}
                  label="Category Image (Optional)"
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button type="submit" variant="primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </Button>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </AdminLayout>
  );
};

export default Categories;
